// [목적] 뱃지 도감 — 획득/미획득 전체 배지 그리드 + 선택 시 조건·진행률 상세 다이얼로그.
// [이유] 2026-09-21 badge-catalog: 프로필 뱃지 탭은 "보유 배지"만 3열로 늘어놓아 (1) 신규 사용자는
//        빈 화면을 보고 (2) 무엇을 하면 배지를 얻는지 알 수 없었다. 미획득 슬롯을 회색으로 함께 보여주면
//        도감 자체가 다음 행동 안내가 된다. 잠금 슬롯 데이터는 BE 카탈로그(GET /users/me/badges/catalog)에서만
//        받는다 — FE mock 금지(ADR-T1-5, ProfilePage.tsx BadgesTab 주석).
// [사이드 이팩트] 데스크톱(ProfilePage)과 모바일(ProfileMobile) 뱃지 탭이 이 컴포넌트를 공유한다 —
//                한쪽만 바꾸면 두 화면이 갈라지므로 수정 시 둘 다 확인할 것.
// [수정 시 고려사항] 라벨/조건 문구는 BE 소유(badge-definitions.ts). 여기서 문구를 하드코딩하지 말 것.
//                   카드 최소 터치 타깃 44px 규칙 유지(2026-09-20 모바일 QA).

import { useState } from 'react';
import { Box, ButtonBase, Dialog, Stack, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { BadgeCatalogItem } from '@gangwoncanvas/types';

import { BadgeMedal } from './BadgeMedal.js';
import {
  BADGE_CATEGORY_LABEL,
  BADGE_TIER_LABEL,
  BADGE_TIER_PALETTE,
  badgeProgressRatio,
  formatBadgeAwardedAt,
  formatBadgeProgress,
} from '../../utils/badgeCatalog.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const SOFT_LINE = c.line.soft[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const ACCENT = c.accent[500][L];
const MONO = 'JetBrains Mono, ui-monospace, monospace';

export interface BadgeCollectionProps {
  items: BadgeCatalogItem[];
  earnedCount: number;
  totalCount: number;
  /** 그리드 최소 칸 폭 — 데스크톱 118, 모바일 100 */
  minTileWidth?: number;
}

/**
 * [목적] 도감 본체 — 상단 요약 바 + 배지 타일 그리드 + 상세 다이얼로그
 * [이유] 요약 바(획득 n/m + 진행 게이지)가 없으면 "몇 개 중 몇 개인지" 를 세어야 한다.
 * [사이드 이팩트] 타일 클릭 → 로컬 state 로 다이얼로그 오픈(라우팅/네트워크 없음).
 */
export function BadgeCollection({
  items,
  earnedCount,
  totalCount,
  minTileWidth = 118,
}: BadgeCollectionProps): JSX.Element {
  const [selected, setSelected] = useState<BadgeCatalogItem | null>(null);
  const collectRatio = totalCount > 0 ? earnedCount / totalCount : 0;

  return (
    <Stack spacing="14px" sx={{ pt: '4px' }}>
      {/* 요약 바 */}
      <Box
        sx={{
          bgcolor: PAPER1,
          border: `1px solid ${SOFT_LINE}`,
          borderRadius: `${tokens.radius.m}px`,
          p: '12px 14px',
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing="8px">
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK_HI }}>뱃지 도감</Typography>
          <Typography sx={{ fontSize: 11.5, color: INK_MID, fontFamily: MONO }}>
            {earnedCount}/{totalCount} 획득
          </Typography>
        </Stack>
        <Box
          role="progressbar"
          aria-label="뱃지 수집률"
          aria-valuenow={earnedCount}
          aria-valuemin={0}
          aria-valuemax={totalCount}
          sx={{
            mt: '8px',
            height: 6,
            borderRadius: 999,
            bgcolor: PAPER2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${Math.round(collectRatio * 100)}%`,
              height: '100%',
              borderRadius: 999,
              background: `linear-gradient(90deg, ${c.badge.bronze.ring[L]} 0%, ${c.badge.gold.ring[L]} 100%)`,
              transition: 'width .3s ease',
            }}
          />
        </Box>
      </Box>

      {/* 타일 그리드 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${minTileWidth}px, 1fr))`,
          gap: '10px',
        }}
      >
        {items.map((item) => (
          <BadgeTile key={item.kind} item={item} onSelect={() => setSelected(item)} />
        ))}
      </Box>

      <BadgeDetailDialog item={selected} onClose={() => setSelected(null)} />
    </Stack>
  );
}

/**
 * [목적] 배지 타일 1칸 — 메달 + 라벨 + (획득일 | 진행률)
 * [이유] 미획득 타일은 회색 메달 + 점선 테두리로 "아직 비어 있는 자리" 임을 형태로도 알린다
 *        (색약 사용자를 위해 색 외에 자물쇠 아이콘·점선이라는 두 번째 단서를 준다).
 */
function BadgeTile({
  item,
  onSelect,
}: {
  item: BadgeCatalogItem;
  onSelect: () => void;
}): JSX.Element {
  const locked = !item.earned;
  const ratio = badgeProgressRatio(item.progress);
  const palette = locked ? BADGE_TIER_PALETTE.locked : BADGE_TIER_PALETTE[item.tier];

  return (
    <ButtonBase
      onClick={onSelect}
      aria-label={`${item.label} 뱃지 ${locked ? '미획득' : '획득'} — ${item.description}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
        p: '16px 10px 13px',
        minHeight: 44,
        width: '100%',
        borderRadius: `${tokens.radius.m}px`,
        bgcolor: locked ? PAPER1 : PAPER0,
        border: locked ? `1px dashed ${BORDER}` : `1px solid ${palette.ring}`,
        boxShadow: locked ? 'none' : `0 1px 2px rgba(0,0,0,.04)`,
        transition: 'transform .18s ease, box-shadow .18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: locked ? `0 2px 8px rgba(0,0,0,.05)` : `0 4px 14px rgba(0,0,0,.09)`,
        },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
      }}
    >
      <BadgeMedal kind={item.kind} tier={item.tier} locked={locked} size={62} />
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 700,
          color: locked ? INK_MID : INK_HI,
          textAlign: 'center',
          lineHeight: 1.3,
        }}
      >
        {item.label}
      </Typography>
      {item.earned ? (
        <Typography sx={{ fontSize: 10.5, color: INK_LOW, fontFamily: MONO }}>
          {formatBadgeAwardedAt(item.awardedAt)}
        </Typography>
      ) : item.progress.target > 1 ? (
        <Stack spacing="4px" sx={{ width: '100%', px: '4px' }}>
          <Box sx={{ height: 4, borderRadius: 999, bgcolor: PAPER2, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${Math.round(ratio * 100)}%`,
                height: '100%',
                borderRadius: 999,
                bgcolor: BADGE_TIER_PALETTE[item.tier].ring,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 10, color: INK_LOW, fontFamily: MONO, textAlign: 'center' }}>
            {formatBadgeProgress(item.progress)}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{ fontSize: 10.5, color: INK_LOW }}>잠김</Typography>
      )}
    </ButtonBase>
  );
}

/**
 * [목적] 배지 상세 — 큰 메달 + 등급/분류 칩 + 조건 문구 + 진행률 + 획득일
 * [이유] 타일에는 조건 전문을 넣을 자리가 없다. 탭/클릭 시 "무엇을 하면 얻는지"를 한 화면에서 끝낸다.
 * [사이드 이팩트] MUI Dialog — focus trap/Esc 닫기/aria-labelledby 자동.
 */
function BadgeDetailDialog({
  item,
  onClose,
}: {
  item: BadgeCatalogItem | null;
  onClose: () => void;
}): JSX.Element {
  const open = item !== null;
  const locked = item ? !item.earned : true;
  const ratio = item ? badgeProgressRatio(item.progress) : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="badge-detail-title"
      PaperProps={{
        sx: {
          borderRadius: `${tokens.radius.l}px`,
          bgcolor: PAPER0,
          maxWidth: 340,
          width: '100%',
          p: '24px 22px 20px',
        },
      }}
    >
      {item && (
        <Stack alignItems="center" spacing="12px">
          <BadgeMedal kind={item.kind} tier={item.tier} locked={locked} size={96} />

          <Stack direction="row" spacing="6px">
            <Chip
              label={BADGE_TIER_LABEL[item.tier]}
              fg={BADGE_TIER_PALETTE[item.tier].fg}
              bg={BADGE_TIER_PALETTE[item.tier].bgFrom}
              border={BADGE_TIER_PALETTE[item.tier].ring}
            />
            <Chip
              label={BADGE_CATEGORY_LABEL[item.category]}
              fg={INK_MID}
              bg={PAPER1}
              border={SOFT_LINE}
            />
          </Stack>

          <Typography
            id="badge-detail-title"
            sx={{ fontSize: 17, fontWeight: 800, color: INK_HI, textAlign: 'center' }}
          >
            {item.label}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: INK_MID, textAlign: 'center', lineHeight: 1.6 }}>
            {item.description}
          </Typography>

          {/* 상태 줄 — 획득이면 획득일, 미획득이면 진행률 게이지 */}
          <Box
            sx={{
              width: '100%',
              mt: '2px',
              p: '12px 14px',
              borderRadius: `${tokens.radius.m}px`,
              bgcolor: PAPER1,
              border: `1px solid ${SOFT_LINE}`,
            }}
          >
            {item.earned ? (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: ACCENT }}>
                  획득 완료
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: INK_MID, fontFamily: MONO }}>
                  {formatBadgeAwardedAt(item.awardedAt)}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing="8px">
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK_MID }}>
                    달성까지
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: INK_MID, fontFamily: MONO }}>
                    {item.progress.target > 1 ? formatBadgeProgress(item.progress) : '조건 미달성'}
                  </Typography>
                </Stack>
                <Box sx={{ height: 6, borderRadius: 999, bgcolor: PAPER2, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      width: `${Math.round(ratio * 100)}%`,
                      height: '100%',
                      borderRadius: 999,
                      bgcolor: BADGE_TIER_PALETTE[item.tier].ring,
                      transition: 'width .3s ease',
                    }}
                  />
                </Box>
                <Typography sx={{ fontSize: 11, color: INK_LOW, lineHeight: 1.5 }}>
                  조건을 채우면 자동으로 획득되고 알림으로 알려드려요.
                </Typography>
              </Stack>
            )}
          </Box>

          <ButtonBase
            onClick={onClose}
            sx={{
              mt: '2px',
              width: '100%',
              height: 44,
              borderRadius: `${tokens.radius.m}px`,
              border: `1px solid ${BORDER}`,
              fontSize: 13,
              fontWeight: 700,
              color: INK_HI,
              '&:hover': { bgcolor: PAPER1 },
            }}
          >
            닫기
          </ButtonBase>
        </Stack>
      )}
    </Dialog>
  );
}

/** 상세 다이얼로그 전용 소형 칩 — 등급/분류 표시 */
function Chip({
  label,
  fg,
  bg,
  border,
}: {
  label: string;
  fg: string;
  bg: string;
  border: string;
}): JSX.Element {
  return (
    <Box
      sx={{
        px: '9px',
        py: '3px',
        borderRadius: 999,
        bgcolor: bg,
        border: `1px solid ${border}`,
        fontSize: 10.5,
        fontWeight: 700,
        color: fg,
        letterSpacing: '.02em',
      }}
    >
      {label}
    </Box>
  );
}
